import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, Modal } from '../components/ui';
import { copyToClipboard } from '../lib/utils';
import { parseServerDate } from '../i18n/format';

interface ApiToken {
  token_id: string;
  name: string;
  description: string | null;
  token_prefix: string;
  scopes: string[];
  created_by: string | null;
  created_at: string;
  expires_at: string | null;
  revoked_at: string | null;
  last_used_at: string | null;
  regenerated_from: string | null;
  status: 'active' | 'expired' | 'revoked';
  days_left: number | null;
}

interface Issued { token: string; name: string; expires_at: string; replaced?: string }

/** Scope groups shown in the picker; `x:write` implies `x:read`, so a group is one choice. */
const SCOPE_GROUPS: Array<{ key: string; label: string; hint: string; read: string; write?: string }> = [
  { key: 'agents', label: 'Agents', hint: 'Provision, suspend and rotate agent credentials', read: 'agents:read', write: 'agents:write' },
  { key: 'endpoints', label: 'Endpoints', hint: 'Enrollment tokens, quarantine, agent releases', read: 'endpoints:read', write: 'endpoints:write' },
  { key: 'access', label: 'AI access', hint: 'Model allow-lists, JIT requests and grants', read: 'access:read', write: 'access:write' },
  { key: 'models', label: 'Models', hint: 'Model registry', read: 'models:read' },
  { key: 'usage', label: 'Usage and cost', hint: 'Token usage, budgets, spend', read: 'usage:read' },
  { key: 'mcp', label: 'MCP directory', hint: 'Servers, tools, discovery, brokers', read: 'mcp:read', write: 'mcp:write' },
  { key: 'incidents', label: 'Incidents', hint: 'Read, update and contain', read: 'incidents:read', write: 'incidents:write' },
  { key: 'audit', label: 'Audit feed', hint: 'Pull audit events for a SIEM', read: 'audit:read' },
  { key: 'webhooks', label: 'Webhooks', hint: 'Event subscriptions', read: 'webhooks:write' },
  { key: 'integrations', label: 'Integrations', hint: 'ITSM connector', read: 'integrations:write' },
  { key: 'iga', label: 'Identity governance', hint: 'SailPoint-style sync and certification callbacks', read: 'iga:read', write: 'iga:write' },
];

type Level = 'none' | 'read' | 'write';
const PRESETS: Array<{ key: string; label: string; levels: (g: string) => Level }> = [
  { key: 'readonly', label: 'Read-only', levels: (g) => (g === 'webhooks' || g === 'integrations' ? 'none' : 'read') },
  { key: 'provisioning', label: 'Provisioning', levels: (g) => (['agents', 'access', 'mcp', 'endpoints'].includes(g) ? 'write' : g === 'models' ? 'read' : 'none') },
  { key: 'soc', label: 'SOC / SIEM', levels: (g) => (g === 'incidents' ? 'write' : ['audit', 'agents', 'endpoints', 'usage'].includes(g) ? 'read' : 'none') },
  { key: 'full', label: 'Full access', levels: () => 'write' },
];

const EXPIRY_CHOICES = [7, 30, 90, 180, 365];

const fmt = (iso: string | null) => (iso ? parseServerDate(iso).toLocaleString() : '—');

const ApiClients: React.FC<{ apiUrl: string }> = ({ apiUrl }) => {
  const { t } = useTranslation();
  const tt = (k: string, d: string) => t(`pages.apiClients.${k}`, { defaultValue: d });

  const [tokens, setTokens] = useState<ApiToken[]>([]);
  const [limits, setLimits] = useState({ def: 30, max: 365 });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [showRevoked, setShowRevoked] = useState(false);
  const [creating, setCreating] = useState(false);
  const [issued, setIssued] = useState<Issued | null>(null);
  const [regen, setRegen] = useState<ApiToken | null>(null);
  const [revoke, setRevoke] = useState<ApiToken | null>(null);
  const [copied, setCopied] = useState(false);

  const api = `${apiUrl}/api/admin/service-tokens`;

  const load = useCallback(async () => {
    try {
      const r = await fetch(api);
      const j = await r.json();
      if (!r.ok) throw new Error(j?.error?.message || `HTTP ${r.status}`);
      setTokens(j.tokens);
      setLimits({ def: j.default_days, max: j.max_days });
      setErr(null);
    } catch (e: any) { setErr(e.message); } finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => tokens.filter((x) => showRevoked || x.status !== 'revoked'), [tokens, showRevoked]);

  const call = async (url: string, method: string, body?: unknown) => {
    const r = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: body === undefined ? undefined : JSON.stringify(body) });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(j?.error?.message || `HTTP ${r.status}`);
    return j;
  };

  // The API reference is dashboard-only: fetch a one-time ticket with the session, then open the docs with it.
  const [docsErr, setDocsErr] = useState<string | null>(null);
  const openReference = async (e: React.MouseEvent) => {
    e.preventDefault();
    setDocsErr(null);
    try {
      const r = await fetch(`${apiUrl}/api/admin/docs-ticket`);
      const j = await r.json();
      if (!r.ok) throw new Error(j?.message || `HTTP ${r.status}`);
      window.open(`${apiUrl.replace(/\/$/, '')}/api/v1/docs?ticket=${j.ticket}`, '_blank', 'noopener');
    } catch (err: any) { setDocsErr(err.message); }
  };

  const scopeLabel = (s: string[]) => (s.includes('*') ? [tt('fullAccess', 'Full access')] : s);

  return (
    <div className="page">
      <h1 className="page-title">{tt('title', 'API clients')}</h1>
      <p style={{ color: 'var(--text-2)', maxWidth: 820 }}>
        {tt('subtitle', 'Service tokens let Terraform, ServiceNow, SailPoint, CI pipelines and SIEMs call the Agenti5 API. Every token has a scope and a time limit, and every call it makes appears in the audit log under its name.')}
        {' '}
        <a href="#" onClick={openReference}>{tt('docs', 'API reference')}</a>
        {docsErr && <span style={{ color: 'var(--danger)' }}> ({docsErr})</span>}
      </p>

      <div className="ent-card" style={{ marginBottom: 16 }}>
        <div className="ent-card-head">
          <span><Icon name="key" /> {tt('tokens', 'Tokens')}</span>
          <span style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <label style={{ fontSize: 12, color: 'var(--text-2)', display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="checkbox" checked={showRevoked} onChange={(e) => setShowRevoked(e.target.checked)} /> {tt('showRevoked', 'Show revoked')}
            </label>
            <button className="ent-btn primary" onClick={() => setCreating(true)}><Icon name="plus" /> {tt('create', 'New API client')}</button>
          </span>
        </div>

        {err && <div style={{ padding: 14, color: 'var(--danger)' }}>{err}</div>}
        {!err && !loading && visible.length === 0 && (
          <div style={{ padding: 18, color: 'var(--text-2)' }}>{tt('empty', 'No API clients yet. Create one to get a bearer token for the API.')}</div>
        )}

        {visible.length > 0 && (
          <div className="ent-table-wrap">
            <table className="ent-table">
              <thead>
                <tr>
                  <th>{tt('name', 'Name')}</th>
                  <th>{tt('scopes', 'Access')}</th>
                  <th>{tt('expires', 'Expires')}</th>
                  <th>{tt('lastUsed', 'Last used')}</th>
                  <th>{tt('status', 'Status')}</th>
                  <th style={{ width: 210 }} />
                </tr>
              </thead>
              <tbody>
                {visible.map((x) => {
                  const soon = x.status === 'active' && x.days_left != null && x.days_left <= 7;
                  return (
                    <tr key={x.token_id} style={x.status === 'active' ? undefined : { opacity: 0.65 }}>
                      <td>
                        <strong>{x.name}</strong>
                        {x.description && <div style={{ color: 'var(--text-2)', fontSize: 12 }}>{x.description}</div>}
                        <div style={{ color: 'var(--text-3)', fontSize: 11, fontFamily: 'monospace' }}>{x.token_prefix}…</div>
                      </td>
                      <td style={{ maxWidth: 280 }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {scopeLabel(x.scopes).map((s) => <span key={s} className="ent-badge" style={{ fontWeight: 400 }}>{s}</span>)}
                        </div>
                      </td>
                      <td style={soon ? { color: 'var(--danger)' } : undefined}>
                        {fmt(x.expires_at)}
                        {x.expires_at == null && <span style={{ color: 'var(--text-2)' }}>{tt('never', 'No expiry (legacy)')}</span>}
                        {x.status === 'active' && x.days_left != null && <div style={{ fontSize: 12 }}>{x.days_left} {tt('daysLeft', 'days left')}</div>}
                      </td>
                      <td>{x.last_used_at ? fmt(x.last_used_at) : <span style={{ color: 'var(--text-3)' }}>{tt('neverUsed', 'Never')}</span>}</td>
                      <td><span className="ent-badge">{x.status === 'active' ? tt('active', 'Active') : x.status === 'expired' ? tt('expired', 'Expired') : tt('revoked', 'Revoked')}</span></td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {x.status !== 'revoked' && (
                          <>
                            <button className="ent-btn sm" onClick={() => setRegen(x)}><Icon name="refresh" /> {tt('regenerate', 'Regenerate')}</button>{' '}
                            <button className="ent-btn sm danger" onClick={() => setRevoke(x)}>{tt('revoke', 'Revoke')}</button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="ent-card">
        <div className="ent-card-head"><span>{tt('usage', 'Using a token')}</span></div>
        <div style={{ padding: 14, fontSize: 13, display: 'grid', gap: 8 }}>
          <div>{tt('usageText', 'Send it as a bearer token. In the API documentation, click Authorize and paste it to use Try it out.')}</div>
          <pre style={{ margin: 0, padding: 10, background: 'var(--surface-2)', borderRadius: 6, overflowX: 'auto' }}>{`curl ${apiUrl.replace(/\/$/, '')}/api/v1/agents -H "Authorization: Bearer ztai_pat_…"`}</pre>
          <div style={{ color: 'var(--text-2)' }}>{tt('limitsText', `Tokens default to ${limits.def} days and can last at most ${limits.max}. Regenerating issues a new secret with a fresh time limit and revokes the old one immediately.`)}</div>
        </div>
      </div>

      {creating && (
        <CreateModal
          limits={limits}
          onClose={() => setCreating(false)}
          onCreate={async (body) => {
            const j = await call(api, 'POST', body);
            setCreating(false); setCopied(false);
            setIssued({ token: j.token, name: j.name, expires_at: j.expires_at });
            load();
          }}
        />
      )}

      {regen && (
        <RegenModal
          token={regen} limits={limits}
          onClose={() => setRegen(null)}
          onRegenerate={async (days) => {
            const j = await call(`${api}/${regen.token_id}/regenerate`, 'POST', { expires_in_days: days });
            setRegen(null); setCopied(false);
            setIssued({ token: j.token, name: j.name, expires_at: j.expires_at, replaced: j.replaced });
            load();
          }}
        />
      )}

      {revoke && (
        <Modal
          title={tt('revokeTitle', 'Revoke token')} icon="key" onClose={() => setRevoke(null)} maxWidth={440}
          footer={<>
            <button className="ent-btn ghost" onClick={() => setRevoke(null)}>{tt('cancel', 'Cancel')}</button>
            <button className="ent-btn danger" onClick={async () => { try { await call(`${api}/${revoke.token_id}`, 'DELETE'); } catch (e: any) { setErr(e.message); } setRevoke(null); load(); }}>{tt('revoke', 'Revoke')}</button>
          </>}
        >
          <p style={{ margin: '0 0 12px' }}>{tt('revokeBody', 'Anything using this token will immediately get 401 errors. This cannot be undone.')}</p>
          <p><strong>{revoke.name}</strong> <span style={{ fontFamily: 'monospace', color: 'var(--text-3)' }}>{revoke.token_prefix}…</span></p>
        </Modal>
      )}

      {issued && (
        <Modal
          title={issued.replaced ? tt('regeneratedTitle', 'New token issued') : tt('createdTitle', 'API client created')} icon="key"
          onClose={() => setIssued(null)} maxWidth={560}
          footer={<button className="ent-btn primary" onClick={() => setIssued(null)}>{tt('done', 'Done')}</button>}
        >
          <div style={{ padding: '10px 12px', borderRadius: 8, marginBottom: 12, fontSize: 13, background: 'color-mix(in srgb, var(--warning, #d98e04) 12%, transparent)', border: '1px solid color-mix(in srgb, var(--warning, #d98e04) 35%, transparent)' }}>
            {tt('onceWarning', 'Copy this token now. It is shown only once and cannot be recovered; if you lose it, regenerate it.')}
            {issued.replaced && ' ' + tt('oldStopped', 'The previous secret has stopped working.')}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input className="ent-input" readOnly value={issued.token} style={{ fontFamily: 'monospace' }} onFocus={(e) => e.target.select()} />
            <button className="ent-btn" onClick={async () => { await copyToClipboard(issued.token); setCopied(true); }}>
              <Icon name={copied ? 'check' : 'copy'} /> {copied ? tt('copied', 'Copied') : tt('copy', 'Copy')}
            </button>
          </div>
          <div style={{ marginTop: 10, color: 'var(--text-2)', fontSize: 13 }}>
            {issued.name} — {tt('expiresOn', 'expires')} {fmt(issued.expires_at)}
          </div>
        </Modal>
      )}
    </div>
  );
};

// ---------------------------------------------------------------- create ----

const CreateModal: React.FC<{
  limits: { def: number; max: number };
  onClose: () => void;
  onCreate: (body: { name: string; description: string; scopes: string[]; expires_in_days: number }) => Promise<void>;
}> = ({ limits, onClose, onCreate }) => {
  const { t } = useTranslation();
  const tt = (k: string, d: string) => t(`pages.apiClients.${k}`, { defaultValue: d });
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [levels, setLevels] = useState<Record<string, Level>>({});
  const [days, setDays] = useState(limits.def);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const scopes = useMemo(() => {
    if (SCOPE_GROUPS.every((g) => levels[g.key] === (g.write ? 'write' : 'read')) && SCOPE_GROUPS.length) return ['*'];
    return SCOPE_GROUPS.flatMap((g) => {
      const l = levels[g.key];
      return l === 'write' && g.write ? [g.write] : l === 'read' || l === 'write' ? [g.read] : [];
    });
  }, [levels]);

  const choices = EXPIRY_CHOICES.filter((d) => d <= limits.max);
  if (!choices.includes(days)) choices.push(days);
  choices.sort((a, b) => a - b);

  const submit = async () => {
    setErr(null);
    if (!name.trim()) return setErr(tt('nameRequired', 'Give the client a name'));
    if (!scopes.length) return setErr(tt('scopeRequired', 'Choose at least one area of access'));
    setBusy(true);
    try { await onCreate({ name: name.trim(), description: description.trim(), scopes, expires_in_days: days }); }
    catch (e: any) { setErr(e.message); setBusy(false); }
  };

  return (
    <Modal
      title={tt('create', 'New API client')} icon="key" onClose={onClose} maxWidth={640}
      footer={<>
        <button className="ent-btn ghost" onClick={onClose} disabled={busy}>{tt('cancel', 'Cancel')}</button>
        <button className="ent-btn primary" onClick={submit} disabled={busy}><Icon name="key" /> {busy ? tt('creating', 'Creating…') : tt('createToken', 'Create token')}</button>
      </>}
    >
      <div className="ent-field">
        <label>{tt('name', 'Name')}</label>
        <input className="ent-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="terraform-prod" autoFocus />
      </div>
      <div className="ent-field">
        <label>{tt('description', 'What is it for? (optional)')}</label>
        <input className="ent-input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Provisions agents from the platform repo" />
      </div>
      <div className="ent-field">
        <label>{tt('expiry', 'Expires after')}</label>
        <select className="ent-select" value={days} onChange={(e) => setDays(Number(e.target.value))}>
          {choices.map((d) => <option key={d} value={d}>{d} {tt('days', 'days')}{d === limits.def ? ` (${tt('default', 'default')})` : ''}</option>)}
        </select>
      </div>
      <div className="ent-field">
        <label>{tt('access', 'Access')}</label>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
          {PRESETS.map((p) => (
            <button key={p.key} type="button" className="ent-btn sm" onClick={() => setLevels(Object.fromEntries(SCOPE_GROUPS.map((g) => {
              const l = p.levels(g.key);
              return [g.key, l === 'write' && !g.write ? 'read' : l];
            })))}>{p.label}</button>
          ))}
          <button type="button" className="ent-btn sm ghost" onClick={() => setLevels({})}>{tt('clear', 'Clear')}</button>
        </div>
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
          {SCOPE_GROUPS.map((g, i) => (
            <div key={g.key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderTop: i ? '1px solid var(--border)' : undefined }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{g.label}</div>
                <div style={{ fontSize: 12, color: 'var(--text-2)' }}>{g.hint}</div>
              </div>
              <select className="ent-select" style={{ width: 160 }} value={levels[g.key] || 'none'} onChange={(e) => setLevels({ ...levels, [g.key]: e.target.value as Level })}>
                <option value="none">{tt('none', 'No access')}</option>
                <option value="read">{g.write ? tt('readOnly', 'Read') : tt('allowed', 'Allowed')}</option>
                {g.write && <option value="write">{tt('readWrite', 'Read and write')}</option>}
              </select>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 6, fontSize: 12, color: 'var(--text-2)' }}>{scopes.length ? scopes.join(', ') : tt('noScopes', 'No access selected')}</div>
      </div>
      {err && <div style={{ color: 'var(--danger)', fontSize: 13 }}>{err}</div>}
    </Modal>
  );
};

// ------------------------------------------------------------ regenerate ----

const RegenModal: React.FC<{
  token: ApiToken; limits: { def: number; max: number };
  onClose: () => void; onRegenerate: (days: number) => Promise<void>;
}> = ({ token, limits, onClose, onRegenerate }) => {
  const { t } = useTranslation();
  const tt = (k: string, d: string) => t(`pages.apiClients.${k}`, { defaultValue: d });
  const [days, setDays] = useState(limits.def);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const choices = EXPIRY_CHOICES.filter((d) => d <= limits.max);
  return (
    <Modal
      title={tt('regenTitle', 'Regenerate token')} icon="refresh" onClose={onClose} maxWidth={460}
      footer={<>
        <button className="ent-btn ghost" onClick={onClose} disabled={busy}>{tt('cancel', 'Cancel')}</button>
        <button className="ent-btn primary" disabled={busy} onClick={async () => { setBusy(true); try { await onRegenerate(days); } catch (e: any) { setErr(e.message); setBusy(false); } }}>
          <Icon name="refresh" /> {tt('regenerate', 'Regenerate')}
        </button>
      </>}
    >
      <p style={{ margin: '0 0 12px' }}>{tt('regenBody', 'This issues a new secret with the same name and access, and the current secret stops working immediately. Update whatever uses it.')}</p>
      <p style={{ margin: '0 0 14px' }}><strong>{token.name}</strong> <span style={{ fontFamily: 'monospace', color: 'var(--text-3)' }}>{token.token_prefix}…</span></p>
      <div className="ent-field">
        <label>{tt('expiry', 'Expires after')}</label>
        <select className="ent-select" value={days} onChange={(e) => setDays(Number(e.target.value))}>
          {choices.map((d) => <option key={d} value={d}>{d} {tt('days', 'days')}</option>)}
        </select>
      </div>
      {err && <div style={{ color: 'var(--danger)', fontSize: 13 }}>{err}</div>}
    </Modal>
  );
};

export default ApiClients;
